import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import HelpTooltip from "./HelpTooltip";
import "./variables.css";
import "./TeamList.css";
import API_URL from "./config";
import useTeamTheme from "./useTeamTheme";
import TradeModal from "./TradeModal";
import PracticeModal from "./PracticeModal";
import RecoverModal from "./RecoverModal";
import LineupModal from "./LineupModal";
import SalaryDistributionModal from "./SalaryDistributionModal";

// Keep roster OVR aligned with Dashboard and gameplay screens.
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.injury, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const MyTeam = () => {
  const [save, setSave] = useState(null);
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showTrade, setShowTrade] = useState(false);
  const [showPractice, setShowPractice] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [showLineup, setShowLineup] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [stamina, setStamina] = useState(0);
  const [staminaProgress, setStaminaProgress] = useState(0);
  const [loadingNextGame, setLoadingNextGame] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useTeamTheme(team);

  const handlePlayNextGame = async () => {
    setLoadingNextGame(true);
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      if (!activeSaveId) {
        alert("No active save file. Please select or create a save.");
        setLoadingNextGame(false);
        return;
      }

      // Fetch save to get team ID
      const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
        headers: { "X-User-ID": userId },
      });

      if (!saveRes.ok) {
        alert("Failed to load save file");
        setLoadingNextGame(false);
        return;
      }

      const saveData = await saveRes.json();
      const teamId = saveData.save.teamId;

      // Fetch team schedule
      const scheduleRes = await fetch(
        `${API_URL}/api/teams/${teamId}/schedule?save_id=${activeSaveId}`
      );

      if (!scheduleRes.ok) {
        alert("Failed to load schedule");
        setLoadingNextGame(false);
        return;
      }

      const scheduleData = await scheduleRes.json();
      const games = scheduleData.games || [];

      // Find first unplayed game
      const nextGame = games.find((g) => !g.played);

      if (!nextGame) {
        alert("No upcoming games available");
        setLoadingNextGame(false);
        return;
      }

      // Navigate to play game with the next game
      navigate("/play-game", { state: { game: nextGame } });
    } catch (err) {
      alert("Error loading next game: " + err.message);
    } finally {
      setLoadingNextGame(false);
    }
  };

  const formatHeight = (h) => {
    if (h === null || h === undefined) return "N/A";
    const n = Number(h);
    if (Number.isNaN(n)) return h;
    const feet = Math.floor(n / 12);
    const inches = n % 12;
    return `${feet}'${inches}"`;
  };

  const formatWeight = (w) => {
    if (!w) return "N/A";
    return `${w} lbs`;
  };

  const handlePractice = () => {
    setShowPractice(true);
  };

  useEffect(() => {
    const loadMyTeam = async () => {
      try {
        setLoading(true);
        setError(""); // Clear any previous errors
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) {
          setError("No active save file. Please select or create a save.");
          setLoading(false);
          return;
        }

        if (!userId) {
          setError("Please log in to view your team");
          setLoading(false);
          return;
        }

        // Fetch save details
        const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: {
            "X-User-ID": userId,
          },
        });

        if (!saveRes.ok) {
          const data = await saveRes.json();
          setError(data.detail || "Failed to load save file");
          setLoading(false);
          return;
        }

        const saveData = await saveRes.json();
        setSave(saveData.save);
        setStamina(saveData.save.stamina || 0);
        setStaminaProgress(saveData.save.stamina_progress || 0);

        const teamId = saveData.save.teamId;
        if (!teamId) {
          setError("No team associated with this save");
          setLoading(false);
          return;
        }

        // Fetch team roster with save-specific records
        const teamRes = await fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`);
        if (!teamRes.ok) {
          const data = await teamRes.json();
          setError(data.detail || "Failed to load team");
          setLoading(false);
          return;
        }

        const teamData = await teamRes.json();
        setTeam(teamData.team);
        setRoster(teamData.roster || []);
      } catch (err) {
        setError(err.message || "Server error");
      } finally {
        setLoading(false);
      }
    };

    loadMyTeam();
  }, [location.pathname]);

  // Check if coming from PlayGame to open lineup modal
  useEffect(() => {
    if (location.state?.openLineupModal) {
      setShowLineup(true);
    }
  }, [location]);

  return (
    <>
      <Navbar />
      <div className="teamlist-container">
        <div className="teamlist-topbar">
          <div className="topbar-content">
            <h1 className="page-title">My Team</h1>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠</span>
            <p>{error}</p>
            <Link to="/saves" style={{ color: "#0366d6", textDecoration: "underline" }}>
              Go to My Saves
            </Link>
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your team...</p>
          </div>
        )}

        {!loading && !error && !save && !team && (
          <div className="error-banner">
            <span className="error-icon">⚠</span>
            <p>Unable to load team data. Please try refreshing the page or returning to your saves.</p>
            <Link to="/saves" style={{ color: "#0366d6", textDecoration: "underline" }}>
              Go to My Saves
            </Link>
          </div>
        )}

        {!loading && save && team && (
          <div className="content-layout">
            {/* Left: Team Details (40%) */}
            <div className="team-details-section">
              <div className="team-card">
                <div className="team-card-header">
                  <div className="team-logo">
                    <span className="team-initial">{team.name?.charAt(0)}</span>
                  </div>
                  <div className="team-name-block" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                    <div>
                      <h2 className="team-name">{team.name}</h2>
                      {team.city && <p className="team-location">{team.city}</p>}
                    </div>
                    <button
                      onClick={handlePlayNextGame}
                      disabled={loadingNextGame}
                      style={{ 
                        padding: "6px 14px",
                        background: "#4caf50",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        textDecoration: "none",
                        fontSize: 13,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: loadingNextGame ? "not-allowed" : "pointer",
                        opacity: loadingNextGame ? 0.6 : 1,
                      }}
                    >
                      ▶ {loadingNextGame ? "Loading..." : "Play Next Game"}
                    </button>
                  </div>
                </div>

                <div className="team-stats">
                  <div className="stat-box">
                    <div className="team-stat-label">Wins</div>
                    <div className="team-stat-value wins-value">{team.wins || 0}</div>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-box">
                    <div className="team-stat-label">Losses</div>
                    <div className="team-stat-value losses-value">{team.losses || 0}</div>
                  </div>
                </div>

                <div className="stamina-bar-wrapper">
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div className="stamina-label">Stamina</div>
                    <HelpTooltip
                      text="Stamina is needed to practice, trade, and heal injured players. Regenerates 1 stamina every 2 games played. Max 3."
                      position="right"
                    />
                  </div>
                  <div className="stamina-bar">
                    {[0, 1, 2].map((index) => {
                      let segmentClass = `stamina-segment ${index < stamina ? "filled" : "empty"}`;
                      if (index === stamina && staminaProgress > 0) {
                        segmentClass += " partial-fill";
                      }
                      return (
                        <div
                          key={index}
                          className={segmentClass}
                          style={
                            index === stamina && staminaProgress > 0
                              ? {
                                  background: `linear-gradient(90deg, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0.3) ${staminaProgress * 50}%, rgba(76, 175, 80, 0.08) ${staminaProgress * 50}%, rgba(76, 175, 80, 0.08) 100%)`,
                                  boxShadow: "0 0 8px rgba(76, 175, 80, 0.4)",
                                }
                              : {}
                          }
                        />
                      );
                    })}
                  </div>
                  <div className="stamina-value">{stamina}/3</div>
                </div>

                <div className="team-actions">
                  <Link to={`/teams/${team._id}/schedule`} className="action-button action-button--full">
                    📅 View Schedule
                  </Link>
                  <div className="team-actions-row">
                    <button 
                      onClick={handlePractice} 
                      className={`action-button ${stamina < 1 ? "disabled" : ""}`}
                      disabled={stamina < 1}
                      title={stamina < 1 ? "Requires 1 stamina" : ""}
                    >
                      🏀 Practice
                    </button>
                    <button 
                      onClick={() => setShowTrade(true)}
                      className={`action-button ${stamina < 1 ? "disabled" : ""}`}
                      disabled={stamina < 1}
                      title={stamina < 1 ? "Requires 1 stamina" : "Trade players with other teams"}
                    >
                      🔄 Trade
                    </button>
                    <button 
                      onClick={() => setShowRecover(true)}
                      className={`action-button ${stamina < 1 ? "disabled" : ""}`}
                      disabled={stamina < 1}
                      title={stamina < 1 ? "Requires 1 stamina" : "Recover injured players"}
                    >
                      💚 Recover
                    </button>
                  </div>
                </div>

                <div className="team-info-footer">
                  <div className="info-item">
                    <span className="info-label">Save File</span>
                    <span className="info-value">{save.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Season</span>
                    <span className="info-value">{save.currentSeason || 2025}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Roster Size</span>
                    <span className="info-value">{roster.length}/15 Players</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Player Roster (60%) */}
            <div className="roster-section">
              <div className="roster-header">
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flex: 1 }}>
                  <h3 className="roster-title">Team Roster</h3>
                  <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
                    <button
                      className="action-button"
                      onClick={() => setShowLineup(true)}
                      title="Create player lineup preset"
                    >
                      ⚙️ Lineup
                    </button>
                    <button
                      className="action-button"
                      onClick={() => setShowSalary(true)}
                      title="View salary distribution"
                    >
                      💰 Salaries
                    </button>
                  </div>
                </div>
                <span className="roster-count">{roster.length}/15 Players</span>
              </div>

              {roster.length === 0 ? (
                <div className="roster-empty">
                  <p>No players found on this roster</p>
                </div>
              ) : (
                <div className="roster-list">
                  {[...roster].sort((a, b) => {
                    return playerOverall(b) - playerOverall(a);
                  }).map((player) => (
                    <Link
                      key={player._id}
                      to={`/players/${player._id}`}
                      state={{ fromPage: "/my-team" }}
                      className="player-row"
                    >
                      <div className="player-row-left">
                        <span className="player-index">#{player.number ?? "?"}</span>
                        <div className="player-avatar">
                          <span className="avatar-text">{player.position || "?"}</span>
                        </div>
                        <div className="player-name-block">
                          <h4 className="player-name">
                            {player.displayName || player.name || "Unknown Player"}
                          </h4>
                          <span className="player-position">{player.position || "N/A"}</span>
                        </div>
                      </div>

                      <div className="player-row-right">
                        <div className="player-stat">
                          <span className="player-stat-label">OVR</span>
                          <span className="player-stat-value">{playerOverall(player)}</span>
                        </div>
                        <div className="player-stat">
                          <span className="player-stat-label">HT</span>
                          <span className="player-stat-value">{formatHeight(player.height)}</span>
                        </div>
                        <div className="player-stat">
                          <span className="player-stat-label">WT</span>
                          <span className="player-stat-value">{formatWeight(player.weight)}</span>
                        </div>
                        {player.stats && (
                          <>
                            <div className="player-stat">
                              <span className="player-stat-label">PPG</span>
                              <span className="player-stat-value">{player.stats.ppg || "N/A"}</span>
                            </div>
                            <div className="player-stat">
                              <span className="player-stat-label">RPG</span>
                              <span className="player-stat-value">{player.stats.rpg || "N/A"}</span>
                            </div>
                            <div className="player-stat">
                              <span className="player-stat-label">APG</span>
                              <span className="player-stat-value">{player.stats.apg || "N/A"}</span>
                            </div>
                          </>
                        )}
                        <div className="player-stat">
                          <span className="player-stat-label">Health</span>
                          <span className="player-stat-value" style={{
                            color: player.injuryType && player.injuryGamesRemaining && player.injuryGamesRemaining > 0 ? "#f87171" : "#4ade80"
                          }}>
                            {player.injuryType && player.injuryGamesRemaining && player.injuryGamesRemaining > 0 ? "Injured" : "Healthy"}
                          </span>
                        </div>

                        <div className="player-arrow">→</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showTrade && (
        <TradeModal
          myRoster={roster}
          myTeamId={team?._id}
          onClose={() => setShowTrade(false)}
          onTradeComplete={() => {
            setShowTrade(false);
            // Refresh stamina and team/roster after trade
            const activeSaveId = localStorage.getItem("activeSaveId");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (activeSaveId && user._id) {
              // Fetch updated save and team data
              fetch(`${API_URL}/api/saves/${activeSaveId}`, {
                headers: { "X-User-ID": user._id },
              })
                .then((res) => res.json())
                .then((data) => {
                  setStamina(data.save.stamina || 0);
                  // Fetch updated roster
                  const teamId = data.save.teamId;
                  return fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`)
                    .then((res) => res.json())
                    .then((teamData) => {
                      setTeam(teamData.team);
                      setRoster(teamData.roster || []);
                    });
                })
                .catch(() => {});
            }
          }}
        />
      )}
      {showLineup && (
        <LineupModal
          roster={roster}
          teamId={team?._id}
          onClose={() => setShowLineup(false)}
        />
      )}
      {showPractice && (
        <PracticeModal
          saveId={save?._id}
          teamId={team?._id}
          stamina={stamina}
          onClose={() => setShowPractice(false)}
          onPracticeComplete={() => {
            setShowPractice(false);
            // Refresh stamina after practice
            const activeSaveId = localStorage.getItem("activeSaveId");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (activeSaveId && user._id) {
              fetch(`${API_URL}/api/saves/${activeSaveId}`, {
                headers: { "X-User-ID": user._id },
              })
                .then((res) => res.json())
                .then((data) => {
                  setStamina(data.save.stamina || 0);
                  // Refresh roster to update injury display
                  const teamId = data.save.teamId;
                  return fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`)
                    .then((res) => res.json())
                    .then((teamData) => {
                      setTeam(teamData.team);
                      setRoster(teamData.roster || []);
                    });
                })
                .catch(() => {});
            }
          }}
        />
      )}
      {showRecover && (
        <RecoverModal
          roster={roster}
          saveId={save?._id}
          teamId={team?._id}
          stamina={stamina}
          onClose={() => setShowRecover(false)}
          onRecoverComplete={() => {
            setShowRecover(false);
            // Refresh stamina and roster after recovery
            const activeSaveId = localStorage.getItem("activeSaveId");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (activeSaveId && user._id) {
              fetch(`${API_URL}/api/saves/${activeSaveId}`, {
                headers: { "X-User-ID": user._id },
              })
                .then((res) => res.json())
                .then((data) => {
                  setStamina(data.save.stamina || 0);
                  // Fetch updated roster
                  const teamId = data.save.teamId;
                  return fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`)
                    .then((res) => res.json())
                    .then((teamData) => {
                      setTeam(teamData.team);
                      setRoster(teamData.roster || []);
                    });
                })
                .catch(() => {});
            }
          }}
        />
      )}
      {showSalary && (
        <SalaryDistributionModal
          roster={roster}
          onClose={() => setShowSalary(false)}
        />
      )}
    </>
  );
};

export default MyTeam;
